<template>
  <div class="image-upload-container">
    <div class="image-list">
      <div
        v-for="(image, index) in modelValue"
        :key="index"
        class="image-item"
      >
        <el-image
          :src="getImageUrl(image)"
          fit="cover"
          :preview-src-list="previewList"
          :initial-index="index"
          :preview-teleported="true"
          class="upload-image"
          style="width: 100px; height: 100px; border-radius: 4px; cursor: pointer"
          crossorigin="anonymous"
          referrerpolicy="no-referrer-when-downgrade"
        />
        <div v-if="isPendingImage(image)" class="pending-badge">
          <el-tag size="small" type="warning">待上传</el-tag>
        </div>
        <div class="image-actions" @click.stop="handleRemove(index)">
          <el-icon class="delete-icon"><Close /></el-icon>
        </div>
      </div>
      <div
        v-if="modelValue.length < maxCount && !uploading"
        class="add-image-btn"
        @click="handleChoose"
      >
        <el-icon class="add-icon"><Plus /></el-icon>
      </div>
      <div v-if="uploading" class="upload-progress">
        <el-progress :percentage="uploadProgress" />
      </div>
    </div>
    <div v-if="showTip" class="upload-tip">{{ tip || `最多上传${maxCount}张图片，点击图片可预览` }}</div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Close, Plus } from '@element-plus/icons-vue'
import { chooseImage, checkDuplicateFile, checkUploadRateLimit, MAX_IMAGE_SIZE, isImageFile, formatFileSize, compressImageFile } from '../utils/fileUtils'
import { ElMessageBox } from 'element-plus'
import { getPreviewUrl } from '../api/file'

const props = defineProps({
  // v-model 绑定的图片列表，可以是字符串数组或对象数组
  modelValue: {
    type: Array,
    default: () => []
  },
  // 最多上传数量
  maxCount: {
    type: Number,
    default: 10
  },
  // 是否显示提示文字
  showTip: {
    type: Boolean,
    default: true
  },
  // 自定义提示文字
  tip: {
    type: String,
    default: ''
  },
  // 业务类型（用于上传）
  businessType: {
    type: String,
    default: ''
  },
  // 模块名称（用于上传）
  module: {
    type: String,
    default: ''
  },
  // 是否立即上传（false时只添加文件对象，不实际上传）
  immediate: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'remove'])

const uploading = ref(false)
const uploadProgress = ref(0)

// 预览列表
const previewList = computed(() => {
  return props.modelValue.map(img => getImageUrl(img))
})

// 获取图片URL（使用代理接口解决COS 403问题）
function getImageUrl(image) {
  let url = ''
  if (typeof image === 'string') {
    url = image
  } else if (image && image.url) {
    url = image.url
  } else if (image && image.file) {
    // 如果是待上传的文件对象，创建预览URL（blob URL不需要代理）
    return URL.createObjectURL(image.file)
  } else {
    return ''
  }
  
  // 如果是blob URL，直接返回（不需要代理）
  if (url.startsWith('blob:')) {
    return url
  }
  
  // 对于COS URL，使用代理接口
  return getPreviewUrl(url)
}

// 判断是否为待上传的图片
function isPendingImage(image) {
  return image && typeof image === 'object' && image.status === 'pending'
}

// 选择图片
const handleChoose = async () => {
  try {
    // 检查数量限制
    if (props.modelValue.length >= props.maxCount) {
      ElMessage.warning(`最多只能上传${props.maxCount}张图片`)
      return
    }

    // 检查上传限流
    if (!checkUploadRateLimit()) {
      ElMessage.warning('上传过于频繁，请等待2分钟后再试！')
      return
    }

    const result = await chooseImage(true)
    
    // 处理返回结果：可能是 FileList 对象（旧版本兼容）或包含 files 和 invalidFiles 的对象
    let files = result
    let invalidFiles = []
    
    if (result && typeof result === 'object' && result.files) {
      // 新版本：返回对象包含 files 和 invalidFiles
      files = result.files
      invalidFiles = result.invalidFiles || []
    } else if (result && result.length !== undefined) {
      // 旧版本兼容：直接是 FileList
      files = result
      invalidFiles = []
    }
    
    // 如果有无效文件，用弹窗提示（停留时间久一点）
    if (invalidFiles && invalidFiles.length > 0) {
      let message = '以下图片不符合要求，已自动过滤：\n\n'
      invalidFiles.forEach((item, index) => {
        message += `${index + 1}. ${item.name}\n   原因：${item.reason}\n`
      })
      message += '\n其他符合要求的图片已加入待上传列表。'
      
      await ElMessageBox.alert(message, '图片大小超限', {
        type: 'warning',
        confirmButtonText: '知道了',
        dangerouslyUseHTMLString: false
      })
    }
    
    const fileArray = Array.from(files)
    const remainingSlots = props.maxCount - props.modelValue.length
    const filesToUpload = fileArray.slice(0, remainingSlots)

    if (filesToUpload.length === 0) return

    uploading.value = true
    uploadProgress.value = 0

    const newImages = [...props.modelValue]
    const initialCount = newImages.length

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i]
      
      console.log(`[ImageUpload] 处理文件: ${file.name}, 大小: ${formatFileSize(file.size)}, 是否为图片: ${isImageFile(file.name)}`)

      // 检查重复文件
      const duplicateCheck = await checkDuplicateFile(file, props.modelValue)
      if (!duplicateCheck.shouldContinue) {
        console.log(`[ImageUpload] 文件 ${file.name} 重复，跳过`)
        continue
      }

      // 文件大小已经在 chooseFile 中检查过了，这里不需要再次检查

      // 对于图片文件，尝试压缩（只有小于2MB的才压缩）
      let processedFile = file
      if (isImageFile(file.name) && file.size <= MAX_IMAGE_SIZE) {
        try {
          console.log(`[ImageUpload] 开始压缩图片: ${file.name}, 原始大小: ${formatFileSize(file.size)}`)
          processedFile = await compressImageFile(file)
          console.log(`[ImageUpload] 压缩完成: ${file.name}, 压缩后大小: ${formatFileSize(processedFile.size)}`)
          // 压缩后再次检查，确保不超过2MB
          if (processedFile.size > MAX_IMAGE_SIZE) {
            console.warn(`[ImageUpload] 压缩后图片 ${file.name} 仍然超过2MB，使用原文件`)
            processedFile = file
          }
        } catch (error) {
          console.warn('[ImageUpload] 图片压缩失败，使用原文件:', error)
          processedFile = file
        }
      }

      // 创建预览URL（使用处理后的文件）
      const previewUrl = URL.createObjectURL(processedFile)

      // 创建图片对象
      const imageItem = {
        file: processedFile, // 使用处理后的文件（压缩后的图片或原文件）
        url: previewUrl,
        status: 'pending' // 待上传状态
      }

      newImages.push(imageItem)
    }

    const addedCount = newImages.length - initialCount
    if (addedCount > 0) {
      emit('update:modelValue', newImages)
      emit('change', newImages)
      ElMessage.success(`已添加 ${addedCount} 张图片${props.immediate ? '' : '，将在提交时上传'}`)
    }

    uploading.value = false
    uploadProgress.value = 0
  } catch (error) {
    uploading.value = false
    uploadProgress.value = 0
    if (error.message !== '用户取消选择' && error.message !== '文件校验失败') {
      console.error('选择图片失败:', error)
      ElMessage.error(error.message || '选择图片失败')
    }
    // 文件校验失败的错误已经在 chooseFile 中显示了，这里不需要重复显示
  }
}

// 移除图片
const handleRemove = (index) => {
  const image = props.modelValue[index]
  
  // 如果是待上传的图片，释放预览URL
  if (isPendingImage(image) && image.url && image.url.startsWith('blob:')) {
    URL.revokeObjectURL(image.url)
  }

  const newImages = [...props.modelValue]
  newImages.splice(index, 1)
  
  emit('update:modelValue', newImages)
  emit('change', newImages)
  emit('remove', index, image)
}

// 处理遮罩层点击关闭预览
const handleMaskClick = (event) => {
  // 检查点击的是遮罩层而不是图片本身
  const target = event.target
  // 如果点击的是遮罩层（.el-image-viewer__mask）而不是画布区域，则关闭预览
  if (target.classList.contains('el-image-viewer__mask')) {
    // 查找关闭按钮并触发点击
    const closeBtn = document.querySelector('.el-image-viewer__close')
    if (closeBtn) {
      closeBtn.click()
    }
  }
}

// 监听预览器打开/关闭
let observer = null
let maskElement = null

onMounted(() => {
  // 使用 MutationObserver 监听预览器的出现
  observer = new MutationObserver(() => {
    const mask = document.querySelector('.el-image-viewer__mask')
    if (mask && mask !== maskElement) {
      // 预览器已打开，添加点击事件
      maskElement = mask
      mask.addEventListener('click', handleMaskClick)
    } else if (!mask && maskElement) {
      // 预览器已关闭，清理引用
      maskElement = null
    }
  })
  
  // 开始观察 DOM 变化
  observer.observe(document.body, {
    childList: true,
    subtree: true
  })
})

onUnmounted(() => {
  // 清理观察器
  if (observer) {
    observer.disconnect()
  }
  
  // 移除事件监听
  if (maskElement) {
    maskElement.removeEventListener('click', handleMaskClick)
    maskElement = null
  }
})

</script>

<style scoped>
.image-upload-container {
  width: 100%;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}

.image-item {
  position: relative;
  width: 100px;
  height: 100px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.image-item:hover .image-actions {
  opacity: 1;
}

.pending-badge {
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 2;
}

.image-actions {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background-color: #f56c6c;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s;
}

.image-actions:hover {
  background-color: #f78989;
  transform: scale(1.1);
}

.delete-icon {
  color: #ffffff;
  font-size: 14px;
  font-weight: bold;
}

.add-image-btn {
  width: 100px;
  height: 100px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
}

.add-image-btn:hover {
  border-color: #409eff;
  color: #409eff;
}

.add-icon {
  font-size: 24px;
  color: #8c939d;
}

.upload-progress {
  width: 100px;
  padding: 20px 0;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

/* 优化预览对话框尺寸 */
:deep(.el-image-viewer__wrapper) {
  .el-image-viewer__canvas {
    img {
      max-width: 60vw;
      max-height: 70vh;
      object-fit: contain;
    }
  }
  
  /* 确保遮罩层可以点击关闭 */
  .el-image-viewer__mask {
    cursor: pointer;
  }
  
  /* 画布区域不触发关闭 */
  .el-image-viewer__canvas {
    cursor: default;
    pointer-events: auto;
  }
}

.upload-image {
  width: 100%;
  height: 100%;
}
</style>

